import React, { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Input from '@components/common/input';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { toast } from 'react-toastify';
import { useLocales } from '@hooks/helpers/useLocales';
import { TProduct } from '@xeppt/xeppt-sdk/types/products';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';
import { EUserType } from '@xeppt/xeppt-sdk/types/user';
import { capitalizeFirstLetter, formatSnakeToSplit } from '@utils/index';

// Helper function to get only numeric values from enum
const getEnumNumericValues = (enumObject: any): number[] => {
    return Object.values(enumObject).filter((value) => typeof value === 'number') as number[];
};

// Product display labels MUST match the consumer portal (xeppt-portal loyalty
// productsName) — keep in sync. Unmapped names fall back to the snake-case transform.
const productLabels: Record<string, string> = {
    order_card_replacement: 'Replace XEPPT prepaid card',
    order_physic_card_first: 'Request XEPPT primary card first order',
    order_physic_card_next: 'Request XEPPT primary card',
    order_supp_physic_card_first: 'Request XEPPT supplementary card first order',
    order_supp_physic_card_next: 'Request XEPPT supplementary card'
};

const ProductsLayout = () => {
    const [activeTab, setActiveTab] = useState<EUserType>(EUserType.INDIVIDUAL);
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);
    const { requestErrorLocale } = useLocales();

    const [isEdit, setIsEdit] = useState(false);
    const [items, setItems] = useState<TProduct[]>([]);
    const form = useForm({});

    const { data: limits, handleRequest: refetchLimits } = useApiQuery({
        method: () => apiAdminService.getServiceProducts(),
        onSuccess: (data) => {
            setItems(data);
            const result: { [key: string]: TProduct } = {};
            data.forEach((item) => {
                result[`${item.name}_${item.level}_${item.user_type}`] = item;
            });
            form.reset(result);
        }
    });

    const { handleRequest: handleUpdateSettings, isLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            const result = Object.keys(values).map((key) => {
                return {
                    ...values[key],
                    price: Number(values[key].price),
                    points: Number(values[key].points)
                };
            });
            return apiAdminService.updateServiceProducts(result);
        },
        onSuccess: () => {
            setIsEdit(false);
            toast.success('Settings were successfully updated');
            refetchLimits();
        },
        onError: requestErrorLocale
    });
    const renderTable = (userType: EUserType, level: ELoyaltyLevel) => {
        if (activeTab !== userType || activeLevel !== level) return null;
        return (
            <table
                className={styles.table}
                style={{
                    display: activeTab === userType && activeLevel === level ? 'table' : 'none'
                }}>
                <tbody>
                    <tr>
                        <td></td>
                        <td>Amount</td>
                        <td>Points</td>
                    </tr>
                    {items.map((item) => {
                        if (item.level !== level || item.user_type !== userType) return null;
                        return (
                            <tr key={item.name}>
                                <td>
                                    {productLabels[item.name] ??
                                        capitalizeFirstLetter(formatSnakeToSplit(item.name))}
                                </td>
                                <td>
                                    <FormField
                                        name={`${item.name}_${item.level}_${item.user_type}.price`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                prefix="CAD"
                                                full
                                                placeholder="0.00"
                                                fixedAmount
                                                hideErrorIcon
                                                {...props}
                                            />
                                        )}
                                    />
                                </td>
                                <td>
                                    <FormField
                                        name={`${item.name}_${item.level}_${item.user_type}.points`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                full
                                                placeholder="0.00"
                                                fixedAmount
                                                hideErrorIcon
                                                {...props}
                                            />
                                        )}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    // Get only numeric values from enums
    const userTypes = getEnumNumericValues(EUserType) as EUserType[];
    const loyaltyLevels = getEnumNumericValues(ELoyaltyLevel) as ELoyaltyLevel[];

    return (
        <div className={styles.wrapper}>
            <FormProvider {...form}>
                <div className={styles.header}>
                    <div className={styles.text_wrapper}>
                        <Typography variant="h4">XEPPT Product</Typography>
                    </div>
                </div>
                <div className={styles.actions_content}>
                    <div className={styles.actions_wrapper}>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.INDIVIDUAL)}
                            variant={
                                activeTab === EUserType.INDIVIDUAL ? 'primary' : 'outlined-dark'
                            }>
                            Personal
                        </Button>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.BUSINESS)}
                            variant={
                                activeTab === EUserType.BUSINESS ? 'primary' : 'outlined-dark'
                            }>
                            Business
                        </Button>
                    </div>
                    <div className={styles.actions}>
                        {!isEdit && (
                            <Button
                                size="normal"
                                variant="outlined"
                                leftIcon="edit"
                                onClick={() => setIsEdit(true)}>
                                Edit
                            </Button>
                        )}
                        {isEdit && (
                            <>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsEdit(false);
                                        form.reset(limits);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateSettings}
                                    isLoading={isLoading}
                                    size="normal"
                                    variant="primary">
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.block}>
                        <div className={styles.divider} />
                        <div className={styles.actions_wrapper}>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.A)}
                                variant={
                                    activeLevel === ELoyaltyLevel.A ? 'primary' : 'outlined-dark'
                                }>
                                Silver
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.B)}
                                variant={
                                    activeLevel === ELoyaltyLevel.B ? 'primary' : 'outlined-dark'
                                }>
                                Gold
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.C)}
                                variant={
                                    activeLevel === ELoyaltyLevel.C ? 'primary' : 'outlined-dark'
                                }>
                                Platinum
                            </Button>
                        </div>

                        {userTypes.map((userType) =>
                            loyaltyLevels.map((level) => (
                                <React.Fragment key={`${userType}-${level}`}>
                                    {renderTable(userType, level)}
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default ProductsLayout;
